import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Navigation,
  Building2
} from "lucide-react";

const PHONE_NUMBER_1 = "tel:+493916357115";
const PHONE_NUMBER_2 = "tel:+493916357222";
const EMAIL = "mailto:info@lackzentrum-wilke.com";
const GOOGLE_MAPS_EMBED = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2436.0!2d11.6!3d52.15!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sLack%20Zentrum%20Wilke!5e0!3m2!1sde!2sde!4v1234567890";
const GOOGLE_MAPS_LINK = "https://www.google.com/maps/dir//Silberbergweg+30,+39128+Magdeburg";

const contactInfo = [
  {
    icon: Building2,
    label: "Firma",
    value: "Lack Zentrum Wilke GmbH",
    link: null
  },
  {
    icon: MapPin,
    label: "Adresse",
    value: "Silberbergweg 30, 39128 Magdeburg",
    link: GOOGLE_MAPS_LINK
  },
  {
    icon: Phone,
    label: "Telefon",
    value: "0391 635 771 15",
    secondaryValue: "0391 635 772 22",
    link: PHONE_NUMBER_1,
    secondaryLink: PHONE_NUMBER_2
  },
  {
    icon: Mail,
    label: "E-Mail",
    value: "info@lackzentrum-wilke.com",
    link: EMAIL
  }
];

const openingHours = [
  { day: "Montag", hours: "07:00 – 17:00" },
  { day: "Dienstag", hours: "07:00 – 17:00" },
  { day: "Mittwoch", hours: "07:00 – 17:00" },
  { day: "Donnerstag", hours: "07:00 – 17:00" },
  { day: "Freitag", hours: "07:00 – 15:00" },
  { day: "Samstag", hours: "Geschlossen" },
  { day: "Sonntag", hours: "Geschlossen" }
];

export const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="kontakt"
      ref={sectionRef}
      className="py-20 lg:py-28 section-light"
    >
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="inline-block text-sm font-semibold text-primary uppercase tracking-wider mb-3">
            Kontakt & Anfahrt
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            So erreichen Sie uns
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Rufen Sie uns an oder senden Sie uns eine E-Mail mit einigen Bildern Ihres Schadens, 
            damit wir Ihnen eine erste Einschätzung geben können.
          </p>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        } transition-opacity duration-700`}>
          {/* Contact Info & Hours */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card className="border-border/50">
              <CardContent className="p-6 space-y-6">
                <h3 className="font-display text-xl font-semibold text-card-foreground">
                  Kontaktdaten
                </h3>
                
                <div className="space-y-4">
                  {contactInfo.map((item, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <item.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">{item.label}</p>
                        {item.link ? (
                          <div>
                            <a
                              href={item.link}
                              className="text-foreground font-medium hover:text-primary transition-colors"
                            >
                              {item.value}
                            </a>
                            {item.secondaryValue && (
                              <>
                                <br />
                                <a
                                  href={item.secondaryLink}
                                  className="text-foreground font-medium hover:text-primary transition-colors"
                                >
                                  {item.secondaryValue}
                                </a>
                              </>
                            )}
                          </div>
                        ) : (
                          <p className="text-foreground font-medium">{item.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Opening Hours Card */}
            <Card className="border-border/50 bg-primary/5">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                    <Clock className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-card-foreground">
                      Öffnungszeiten
                    </h3>
                    <p className="text-sm text-muted-foreground">Mo-Fr geöffnet</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {openingHours.map((item, index) => (
                    <div
                      key={index}
                      className={`flex justify-between py-2 ${
                        index < openingHours.length - 1 ? 'border-b border-border/50' : ''
                      }`}
                    >
                      <span className="text-muted-foreground">{item.day}</span>
                      <span className={`font-medium ${
                        item.hours === 'Geschlossen' ? 'text-muted-foreground' : 'text-foreground'
                      }`}>
                        {item.hours}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="primary" size="lg" asChild className="gap-2 flex-1">
                <a href={PHONE_NUMBER_1}>
                  <Phone className="h-4 w-4" />
                  Jetzt anrufen
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild className="gap-2 flex-1">
                <a href={EMAIL}>
                  <Mail className="h-4 w-4" />
                  E-Mail mit Schadensbildern
                </a>
              </Button>
            </div>
          </div>

          {/* Map */}
          <div className="space-y-4">
            <Card className="border-border/50 overflow-hidden">
              <div className="aspect-[4/3] w-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2436.7!2d11.5938!3d52.1577!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47a5f1b5c8e5c5c5%3A0x1234567890abcdef!2sSilberbergweg%2030%2C%2039128%20Magdeburg!5e0!3m2!1sde!2sde!4v1234567890123"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Standort Lack Zentrum Wilke"
                  className="w-full h-full"
                />
              </div>
            </Card>

            <Button
              variant="outline"
              size="lg"
              asChild
              className="w-full gap-2"
            >
              <a href={GOOGLE_MAPS_LINK} target="_blank" rel="noopener noreferrer">
                <Navigation className="h-4 w-4" />
                Route planen
              </a>
            </Button>

            {/* Info Box */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Tipp:</strong> Senden Sie uns vorab Bilder 
                Ihres Schadens per E-Mail, damit wir Ihnen schnell eine erste Einschätzung 
                geben können.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
